/**
 * SAP backend option — public entry point.
 *
 * Part of the isolated `sap-backend/` side project (see README.md). This module
 * is the single thing `dataService.ts` imports. It exposes the same function
 * names dataService dispatches to, so flipping an entity to SAP is a one-line
 * change at the call site:
 *
 *     if (isSapMode()) return sapBackend.getPeriodicActuals(projectId)
 *
 * Entities not yet implemented are absent here; `dataService` only routes the
 * ones listed as 'sap' in config.ENTITY_STATUS, so there is no risk of calling
 * a missing function.
 *
 * ISOLATION: no Supabase imports anywhere under this folder. The only sibling
 * services it touches (wbsActualsService) are themselves SAP clients.
 */

import { getPeriodicActuals } from './entities/actuals'
import { getFinancialActuals } from './entities/financialActuals'
import { getWbsElements } from './entities/wbs'
import { getProjects } from './entities/projects'
import * as cap from './entities/cap'
import { isEntityOnSap, sapFallbackEnabled } from './config'
import { SapNotImplementedError } from './errors'

export const sapBackend = {
  // Direct S/4 read services (live ACDOCA / PRPS / PROJ)
  getPeriodicActuals,
  getFinancialActuals,
  getWbsElements,
  getProjects,
  // App-owned entities from the CAP service (/ppm, HANA Cloud) — plan §9B
  ...cap,
}

export type SapBackend = typeof sapBackend

/** Names of the dataService functions currently served by SAP. */
export function implementedEntities(): string[] {
  return Object.keys(sapBackend)
}

export { isEntityOnSap, sapFallbackEnabled, SapNotImplementedError }
